import { Link } from "react-router-dom";
import { Gift, ArrowRight, Sparkles } from "lucide-react";

/**
 * Prominent Refer & Earn CTA — shown on every user dashboard right after the
 * quick-stat grid. Links to the existing /refer page (single source of truth).
 *
 * Compliance-safe: never claims "guaranteed income". Uses "eligible rewards"
 * and cites GPM T&C. Visually distinct via the navy → gold gradient so users
 * discover the program at a glance.
 *
 * @param {"dark"|"light"} theme  Dark for navy-bg dashboards, light for cream/white dashboards.
 */
export default function ReferEarnCard({ theme = "dark" }) {
    const isDark = theme === "dark";
    const wrapper = isDark
        ? "relative overflow-hidden rounded-2xl border border-gold-500/50 bg-gradient-to-br from-navy-500 via-[#0b2545] to-navy-500 shadow-[0_0_28px_rgba(218,165,32,0.18)] hover:shadow-[0_0_40px_rgba(218,165,32,0.28)] transition-shadow"
        : "relative overflow-hidden rounded-2xl border-2 border-gold-500/60 bg-gradient-to-br from-white via-gold-500/5 to-gold-500/10 hover:border-gold-500 transition-colors";
    const titleColor = isDark ? "text-white" : "text-navy-500";
    const bodyColor = isDark ? "text-white/80" : "text-navy-500/75";
    const microColor = isDark ? "text-white/45" : "text-navy-500/55";
    const ctaClass = "inline-flex items-center gap-1.5 h-10 px-5 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold text-sm transition-colors";
    return (
        <Link to="/refer" data-testid="refer-earn-card" className={`block group ${wrapper}`}>
            {/* Decorative gold sparkle in the top-right — visible on both themes */}
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-gold-500/15 blur-2xl pointer-events-none" />
            <div className="relative p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDark ? "bg-gold-500/20 border border-gold-500/40" : "bg-gold-500/15 border border-gold-500/40"}`}>
                    <Gift className="w-7 h-7 text-gold-500" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-gold-500 font-extrabold">🎁 Refer &amp; Earn</span>
                        <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest ${microColor}`}><Sparkles className="w-3 h-3 text-gold-500" /> New</span>
                    </div>
                    <h3 className={`font-heading text-lg sm:text-xl font-bold ${titleColor} mt-1`}>Share &amp; earn eligible rewards</h3>
                    <p className={`text-xs sm:text-sm ${bodyColor} mt-1 leading-relaxed`}>Share verified GPM properties &amp; services with friends. Track referrals, leads and eligible commission — all from one dashboard.</p>
                    <p className={`text-[10px] ${microColor} mt-1.5`}>* Rewards are based on qualifying activity and subject to GPM Terms &amp; Conditions.</p>
                </div>
                <div className="w-full sm:w-auto">
                    <span data-testid="refer-earn-cta" className={ctaClass}>Explore Refer &amp; Earn <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
                </div>
            </div>
        </Link>
    );
}
